/** @jsx jsx */
import { useMemo} from 'react';
import { css, jsx } from '@emotion/react';
import { LegendItem, LegendItemProps } from './LegendItem';
import { forwardRef } from 'react';
import Paginator from './Paginator';
import LegendTitle from './LegendTitle';
import { ContextMenu2 } from '@blueprintjs/popover2';
import LegendRightClickMenu from './LegendRightClickMenu';
import { WidgetControlsDispatchType } from 'views/common/WidgetControls/types';
import { useEffect } from 'react';

/**
 * A horizontal legend. Only displays when the data contains labels.
 * "" is not a label.
 * @param data       [{key:string} | string] where key is what will be
 *                   displayed in the legend
 * @param colorScale (key) => color
 * @param mouseOver  (key) => any
 * @param mouseOut   () => any
 * @param sm         use small width
 * @param lg         use large width
 */
export const Legend = forwardRef<HTMLDivElement,LegendProps>(({data, 
						colorScale, 
						keyValues,
						mouseOver, 
						mouseOut,
						onClick,
						sm = false, 
						lg = false,
						dispatch },ref) => {
	const sizeStyle = sm ? smallStyle : lg ? largeStyle : mediumStyle;
	let dataTyped: ({ key: string } | string)[] = data;

	// True if the data included any label info
	const hasLabels:boolean = useMemo(()=> {
		const combinedLabels = dataTyped?.reduce((p,n)=>{
			if (typeof n == "string") return n + p
			if (typeof n == "object") return p+n.key
			return ""
		},"")
		return combinedLabels ? true : false
	},[dataTyped])

	const legendItemsProps: LegendItemProps[] = useMemo(()=>{
		return dataTyped.map((d) => {
			const key = typeof d === 'string' 
				? d 
				: (d as { key: string }).key;
			return {
				key:key,
				text:key,
				enabled:keyValues ? keyValues[key] : true,
				color:colorScale(key),
				mouseOver:mouseOver,
				mouseOut:mouseOut,
				onClick:()=>onClick(key)
			}
		})
	},[dataTyped,mouseOver,mouseOut,colorScale,keyValues,onClick])
	useEffect(()=>{
		dispatch({type:"setLegendItemProps",payload:legendItemsProps})
	},[legendItemsProps,dispatch])

	const rightClickMenu = <LegendRightClickMenu dispatch={dispatch}/>

	return (
		// Only return a legend if labels were given
		hasLabels && <ContextMenu2 	ref={ref} 
									css={legendContainerStyle}
									content={rightClickMenu}>
			<Paginator	childrenProps={legendItemsProps}
						childRenderFunc={LegendItem}
						css={[legendStyle, 
							sizeStyle, 
							mouseOut && mouseOver ? interactiveStyle 
												  : null]}/>
		</ContextMenu2>
	);
});

interface LegendProps {
	data: { key: string }[] | string[];
	/**
	 * These values indicate whether or not a given
	 * key is enabled. Disabled keys will appear
	 * greyed out.
	 */
	keyValues?: {[key:string]:boolean};
	colorScale;
	showTitle?:boolean;
	ref?;
	mouseOver?;
	/** 
	 * The function that will be called by a legend item
	 * when it is clicked
	 */
	onClick?: (string)=>unknown;
	mouseOut?;
	sm?;
	md?;
	lg?;
	dispatch:WidgetControlsDispatchType;
}

const legendContainerStyle = css`
	display: flex;
    flex-direction: column;
	height:100%
`

const legendStyle = css`
	display: flex;
	flex-direction: column;
	flex-wrap: nowrap;
	justify-content: flex-start;
	color: #666666
`;

const interactiveStyle = css`
	.legendItem:hover {
		background-color: #e4e4e4;
	}
`;

const legendWidthStyle = (legendRemWidth) => css`
	width: ${legendRemWidth}px;
`;
export const SMALL_STYLE_WIDTH = 112;
const smallStyle = legendWidthStyle(SMALL_STYLE_WIDTH);
export const MEDIUM_STYLE_WIDTH = 144;
const mediumStyle = legendWidthStyle(MEDIUM_STYLE_WIDTH);
export const LARGE_STYLE_WIDTH = 192;
const largeStyle = legendWidthStyle(LARGE_STYLE_WIDTH);
